/**
 * The session store.
 *
 * One zustand store holds the exercise, the settings, the undo history and the
 * derived legality picture. Derived data is recomputed inside the actions that
 * invalidate it rather than in a selector, because the computation is the
 * expensive part of an edit and should happen exactly once per edit.
 *
 * Feedback philosophy, enforced here: `report` is null until the student presses
 * Grade, and any edit throws it away again. Nothing in this store tells the UI
 * that a placed note is wrong before then. The one exception is the allowed-notes
 * assist, whose dead-end warnings are alarms about the corner you have painted,
 * not verdicts on the notes you have written.
 */

import { create } from 'zustand';
import {
  computeLegality,
  createExercise,
  adjacentPitch,
  clampToWindow,
  edoOf,
  grade,
  keyById,
  KEYS,
  keysOfMode,
  legalSet,
  measureCount,
  pitchWindow,
  randomOf,
  withNote,
  type AssistId,
  type CfPosition,
  type Exercise,
  type GradeReport,
  type Legality,
  type SessionMeta,
} from '../core';
import { audio } from '../audio/engine';
import { midiOf } from '../core';
import { DEFAULT_SETTINGS, type Settings } from './settings';
import { loadSession, loadSettings, saveSession, saveSettings } from './persistence';

/** Where the keyboard caret sits: a measure, and a pitch within its window. */
export interface Cursor {
  measure: number;
  pitch: number;
}

export interface SessionState {
  exercise: Exercise;
  settings: Settings;
  /** Assists that were engaged at any point while notes were placed. */
  assisted: Record<AssistId, boolean>;
  /** The grade report, or null while judgment is withheld. */
  report: GradeReport | null;
  legality: Legality;

  /** Undo stack of counterpoint snapshots for the current exercise. */
  history: (number | null)[][];
  historyIndex: number;

  cursor: Cursor;
  /** True once the keyboard has been used, so the caret is only drawn for those who need it. */
  keyboardMode: boolean;
  /** Measure the pointer is over, or null. Drives the dead-end explanation. */
  hoverMeasure: number | null;
  /** Measure playback starts from. */
  playStart: number;

  newExercise: () => void;
  placeNote: (measure: number, pitch: number) => void;
  eraseNote: (measure: number) => void;
  /** Place, or lift the note if that exact pitch is already written there. */
  toggleNote: (measure: number, pitch: number) => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  gradeNow: () => void;
  updateSettings: (patch: Partial<Settings>) => void;
  toggleAssist: (id: AssistId) => void;
  moveCursor: (delta: number) => void;
  moveCursorPitch: (delta: number) => void;
  setCursor: (measure: number, pitch?: number) => void;
  /** Write the note under the caret, or lift it if it is already there. */
  commitCursor: () => void;
  setHoverMeasure: (measure: number | null) => void;
  /** Record which input the student is currently driving with. */
  setKeyboardMode: (on: boolean) => void;
  setPlayStart: (measure: number) => void;
  loadExercise: (exercise: Exercise, patch?: Partial<Settings>) => void;
  /** True when the student has written anything worth confirming before discarding. */
  isDirty: () => boolean;
  /** The metadata bundle the serialisers want. */
  meta: () => SessionMeta;
}

/** Build a fresh exercise honouring the key and placement preferences. */
function exerciseFromSettings(settings: Settings): Exercise {
  const key =
    settings.keyPreference === 'random'
      ? randomOf(keysOfMode(Math.random() < 0.5 ? 'major' : 'minor'))
      : keyById(settings.keyPreference);
  const cfPos: CfPosition =
    settings.cfPreference === 'random' ? randomOf(['lower', 'upper'] as const) : settings.cfPreference;
  return createExercise(key, cfPos, settings.profile, settings.layout === 'single', settings.edo);
}

/** Recompute the legality picture for a position. */
const legalityFor = (ex: Exercise, settings: Settings): Legality =>
  computeLegality(ex, settings.profile, settings.assists.allowed);

/** A sensible caret pitch for a measure: the note written there, else a legal one. */
function defaultPitch(ex: Exercise, settings: Settings, measure: number, near?: number): number {
  const written = ex.cp[measure];
  if (written != null) return written;
  const candidates = legalSet(ex, measure, settings.profile);
  const window = pitchWindow(ex, measure);
  const target = near ?? Math.round((window.lo + window.hi) / 2);
  if (!candidates.length) return clampToWindow(ex, measure, target);
  return candidates.reduce((best, c) => (Math.abs(c.d - target) < Math.abs(best - target) ? c.d : best), candidates[0].d);
}

/** Sound the vertical interval of one measure — the placement audition. */
function auditionMeasure(ex: Exercise, measure: number): void {
  const at = audio.now() + 0.02;
  if (ex.cf[measure] != null) audio.note(midiOf(ex.cf[measure], measure, ex), at, 0.8, 'cf');
  if (ex.cp[measure] != null) audio.note(midiOf(ex.cp[measure]!, measure, ex), at, 0.8, 'cp');
}

/** Build the initial state, restoring the autosaved session when there is one. */
function initialState() {
  const storedSettings = typeof window === 'undefined' ? DEFAULT_SETTINGS : loadSettings();
  audio.volume = storedSettings.volume;
  audio.clickVolume = storedSettings.clickVolume;
  audio.clickVoice = storedSettings.clickVoice;

  const saved = typeof window === 'undefined' ? null : loadSession();
  const exercise = saved?.exercise ?? exerciseFromSettings(storedSettings);
  const settings = { ...storedSettings, edo: edoOf(exercise) };
  return {
    exercise,
    settings,
    assisted: saved?.assisted ?? { labels: false, allowed: false },
    report: null as GradeReport | null,
    legality: legalityFor(exercise, settings),
    history: [exercise.cp.slice()],
    historyIndex: 0,
    cursor: { measure: 0, pitch: defaultPitch(exercise, settings, 0) },
    keyboardMode: false,
    hoverMeasure: null as number | null,
    playStart: 0,
  };
}

export const useSession = create<SessionState>((set, get) => ({
  ...initialState(),

  newExercise: () => {
    const { settings } = get();
    const exercise = exerciseFromSettings(settings);
    set({
      exercise,
      assisted: { labels: false, allowed: false },
      report: null,
      legality: legalityFor(exercise, settings),
      history: [exercise.cp.slice()],
      historyIndex: 0,
      cursor: { measure: 0, pitch: defaultPitch(exercise, settings, 0) },
      playStart: 0,
      hoverMeasure: null,
    });
    saveSession({ exercise, assisted: { labels: false, allowed: false } });
  },

  placeNote: (measure, pitch) => {
    const { exercise, settings, assisted } = get();
    if (measure < 0 || measure >= measureCount(exercise)) return;
    const exercise2 = withNote(exercise, measure, pitch);
    // Engaging an assist at any point while writing marks its dimensions assisted.
    const assisted2: Record<AssistId, boolean> = {
      labels: assisted.labels || settings.assists.labels,
      allowed: assisted.allowed || settings.assists.allowed,
    };
    commit(set, get, exercise2, assisted2);
    if (settings.auditionOnPlace) auditionMeasure(exercise2, measure);
  },

  eraseNote: (measure) => {
    const { exercise, assisted } = get();
    if (exercise.cp[measure] == null) return;
    commit(set, get, withNote(exercise, measure, null), assisted);
  },

  toggleNote: (measure, pitch) => {
    const { exercise } = get();
    if (exercise.cp[measure] === pitch) get().eraseNote(measure);
    else get().placeNote(measure, pitch);
  },

  undo: () => stepHistory(set, get, -1),
  redo: () => stepHistory(set, get, +1),
  canUndo: () => get().historyIndex > 0,
  canRedo: () => get().historyIndex < get().history.length - 1,

  gradeNow: () => {
    const { exercise, settings } = get();
    set({ report: grade(exercise, settings.profile) });
  },

  updateSettings: (patch) => {
    const settings = { ...get().settings, ...patch };
    saveSettings(settings);
    audio.volume = settings.volume;
    audio.clickVolume = settings.clickVolume;
    audio.clickVoice = settings.clickVoice;

    const { exercise } = get();
    // A rules change re-derives what is legal and retracts any stale grade.
    const rulesChanged = patch.profile != null && patch.profile !== get().settings.profile;
    set({
      settings,
      legality: legalityFor(exercise, settings),
      report: rulesChanged ? null : get().report,
    });
  },

  toggleAssist: (id) => {
    const { settings } = get();
    get().updateSettings({ assists: { ...settings.assists, [id]: !settings.assists[id] } });
  },

  moveCursor: (delta) => {
    const { exercise, settings, cursor } = get();
    const n = measureCount(exercise);
    const measure = Math.min(Math.max(cursor.measure + delta, 0), n - 1);
    if (measure === cursor.measure) return;
    set({
      keyboardMode: true,
      cursor: { measure, pitch: defaultPitch(exercise, settings, measure, cursor.pitch) },
    });
  },

  moveCursorPitch: (delta) => {
    const { exercise, cursor } = get();
    const pitch = adjacentPitch(exercise, cursor.measure, cursor.pitch, delta);
    set({ keyboardMode: true, cursor: { ...cursor, pitch } });
  },

  setCursor: (measure, pitch) => {
    const { exercise, settings } = get();
    const n = measureCount(exercise);
    const m = Math.min(Math.max(measure, 0), n - 1);
    set({ cursor: { measure: m, pitch: pitch ?? defaultPitch(exercise, settings, m) } });
  },

  commitCursor: () => {
    const { cursor } = get();
    set({ keyboardMode: true });
    get().toggleNote(cursor.measure, cursor.pitch);
  },

  setHoverMeasure: (measure) => {
    if (get().hoverMeasure === measure) return;
    set({ hoverMeasure: measure });
  },

  setKeyboardMode: (on) => {
    if (get().keyboardMode === on) return;
    set({ keyboardMode: on });
  },

  setPlayStart: (measure) => set({ playStart: measure }),

  loadExercise: (exercise, patch) => {
    const settings = { ...get().settings, ...patch, edo: edoOf(exercise) };
    if (patch) saveSettings(settings);
    set({
      exercise,
      settings,
      assisted: { labels: false, allowed: false },
      report: null,
      legality: legalityFor(exercise, settings),
      history: [exercise.cp.slice()],
      historyIndex: 0,
      cursor: { measure: 0, pitch: defaultPitch(exercise, settings, 0) },
      playStart: 0,
    });
    saveSession({ exercise, assisted: { labels: false, allowed: false } });
  },

  isDirty: () => get().exercise.cp.some((d) => d != null),

  meta: () => {
    const { settings, assisted } = get();
    return {
      profile: settings.profile,
      singleStaff: settings.layout === 'single',
      assisted,
      assists: settings.assists,
    };
  },
}));

/* ---- internals ---------------------------------------------------------- */

type Set = (partial: Partial<SessionState>) => void;
type Get = () => SessionState;

/**
 * Apply an edit: store it, push it onto the undo stack, recompute legality,
 * retract any stale grade, and autosave.
 */
function commit(set: Set, get: Get, exercise: Exercise, assisted: Record<AssistId, boolean>): void {
  const { settings, history, historyIndex, cursor } = get();
  const trimmed = history.slice(0, historyIndex + 1);
  trimmed.push(exercise.cp.slice());
  set({
    exercise,
    assisted,
    report: null,
    legality: legalityFor(exercise, settings),
    history: trimmed,
    historyIndex: trimmed.length - 1,
    cursor: { ...cursor, pitch: exercise.cp[cursor.measure] ?? cursor.pitch },
  });
  saveSession({ exercise, assisted });
}

/** Move through the undo history without disturbing the assist record. */
function stepHistory(set: Set, get: Get, delta: number): void {
  const { history, historyIndex, exercise, settings, assisted } = get();
  const next = historyIndex + delta;
  if (next < 0 || next >= history.length) return;
  const restored: Exercise = { ...exercise, cp: history[next].slice() };
  set({
    exercise: restored,
    historyIndex: next,
    report: null,
    legality: legalityFor(restored, settings),
  });
  saveSession({ exercise: restored, assisted });
}

/** Every key id, for the key chooser. */
export const ALL_KEYS = KEYS;
